/**
 * Admin Dashboard (Institution Admin)
 *
 * Organization overview, user management, and system statistics.
 * Dense grid layout for efficient administration.
 */

import { Link } from 'react-router-dom';
import styles from './AdminDashboard.module.css';

// Role types for user counts
export type UserRoleType = 'student' | 'teacher' | 'institution_admin' | 'super_admin';

// User count by role
export interface UserCount {
  role: UserRoleType;
  label: string;
  count: number;
}

// Organization information
export interface OrganizationInfo {
  id: string;
  name: string;
  domain: string | null;
  createdAt: Date;
  subscriptionTier: 'trial' | 'basic' | 'pro' | 'enterprise';
  licenseCount: number;
  licenseUsed: number;
}

// Quick statistics
export interface QuickStats {
  experimentsAvailable: number;
  activeAssignments: number;
  completedLabs: number;
  avgScore: number;
}

// Quick action link
export interface AdminAction {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  to: string;
}

export interface AdminDashboardProps {
  organization?: OrganizationInfo | null;
  userCounts?: UserCount[];
  quickStats?: QuickStats | null;
  actions?: AdminAction[];
  isSuperAdmin?: boolean;
}

export function AdminDashboard({
  organization = null,
  userCounts = [],
  quickStats = null,
  actions = [],
  isSuperAdmin = false,
}: AdminDashboardProps) {

  // Default quick actions
  const defaultActions: AdminAction[] = [
    {
      id: 'users',
      title: 'User Management',
      description: 'Manage users, roles, and permissions',
      icon: (
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      to: '/admin/users',
    },
    {
      id: 'classes',
      title: 'Classes & Groups',
      description: 'Organize students into classes',
      icon: (
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      to: '/admin/classes',
    },
    {
      id: 'settings',
      title: 'Settings',
      description: 'Configure organization settings',
      icon: (
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      to: '/admin/settings',
    },
    {
      id: 'reports',
      title: 'Reports',
      description: 'View usage and performance reports',
      icon: (
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      to: '/admin/reports',
    },
  ];

  const adminActions = actions.length > 0 ? actions : defaultActions;

  // Default user counts (empty state)
  const defaultUserCounts: UserCount[] = [
    { role: 'student', label: 'Students', count: 0 },
    { role: 'teacher', label: 'Teachers', count: 0 },
    { role: 'institution_admin', label: 'Admins', count: 0 },
  ];

  const displayUserCounts = userCounts.length > 0 ? userCounts : defaultUserCounts;

  // Default stats
  const displayStats = quickStats ?? {
    experimentsAvailable: 0,
    activeAssignments: 0,
    completedLabs: 0,
    avgScore: 0,
  };

  // Calculate total users
  const totalUsers = displayUserCounts.reduce((sum, uc) => sum + uc.count, 0);

  // License usage percentage
  const licenseUsagePercent = organization
    ? Math.round((organization.licenseUsed / organization.licenseCount) * 100)
    : 0;

  return (
    <div className={styles.container}>
      <main className={styles.main}>
        {/* Header */}
        <header className={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
            <h1 className={styles.headerTitle}>Admin Dashboard</h1>
            {isSuperAdmin && <span className={styles.superAdminBadge}>Super Admin</span>}
          </div>
          <p className={styles.headerSubtitle}>
            Manage users, classes, and organization settings
          </p>
        </header>

        {/* Empty State - No Organization */}
        {!organization && (
          <div className={styles.emptyState}>
            <svg className={styles.emptyStateIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h2 className={styles.emptyStateTitle}>No Organization</h2>
            <p className={styles.emptyStateText}>
              Your account is not associated with an organization yet.
              Please contact support or create a new organization.
            </p>
          </div>
        )}

        {organization && (
          <div className={styles.dashboardGrid}>
            {/* Organization Overview */}
            <section className={`${styles.card} ${styles.orgOverview}`}>
              <div className={styles.cardHeader}>
                <h2 className={styles.cardTitle}>Organization Overview</h2>
              </div>
              <div className={styles.orgInfo}>
                <div className={styles.orgInfoSection}>
                  <span className={styles.orgInfoLabel}>Organization Name</span>
                  <span className={styles.orgInfoValue}>{organization.name}</span>
                </div>
                {organization.domain && (
                  <div className={styles.orgInfoSection}>
                    <span className={styles.orgInfoLabel}>Domain</span>
                    <span className={`${styles.orgInfoValue} ${styles.mono}`}>{organization.domain}</span>
                  </div>
                )}
                <div className={styles.orgInfoSection}>
                  <span className={styles.orgInfoLabel}>Subscription</span>
                  <span className={styles.orgInfoValue}>
                    {organization.subscriptionTier.charAt(0).toUpperCase() + organization.subscriptionTier.slice(1)}
                  </span>
                </div>
                <div className={styles.orgInfoSection}>
                  <span className={styles.orgInfoLabel}>License Usage</span>
                  <span className={`${styles.orgInfoValue} ${styles.mono}`}>
                    {organization.licenseUsed} / {organization.licenseCount} ({licenseUsagePercent}%)
                  </span>
                </div>
                <div className={styles.orgInfoSection}>
                  <span className={styles.orgInfoLabel}>Member Since</span>
                  <span className={styles.orgInfoValue}>
                    {organization.createdAt.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                  </span>
                </div>
              </div>
            </section>

            {/* Stats Grid */}
            <section className={styles.statsGrid}>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Total Users</span>
                <span className={styles.statValue}>{totalUsers}</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Students</span>
                <span className={styles.statValue}>
                  {displayUserCounts.find(uc => uc.role === 'student')?.count ?? 0}
                </span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Teachers</span>
                <span className={styles.statValue}>
                  {displayUserCounts.find(uc => uc.role === 'teacher')?.count ?? 0}
                </span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Experiments</span>
                <span className={styles.statValue}>{displayStats.experimentsAvailable}</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Active Assignments</span>
                <span className={styles.statValue}>{displayStats.activeAssignments}</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Completed Labs</span>
                <span className={styles.statValue}>{displayStats.completedLabs}</span>
              </div>
            </section>

            {/* User Counts by Role */}
            <section className={`${styles.card} ${styles.userCounts}`}>
              <div className={styles.cardHeader}>
                <h2 className={styles.cardTitle}>Users by Role</h2>
                <Link to="/admin/users" className={styles.cardAction}>Manage</Link>
              </div>
              <div className={styles.userCountList}>
                {displayUserCounts.map((userCount) => (
                  <div key={userCount.role} className={styles.userCountItem}>
                    <div className={styles.userCountRole}>
                      <svg className={styles.userCountIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className={styles.userCountName}>{userCount.label}</span>
                    </div>
                    <span className={styles.userCountValue}>{userCount.count}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Quick Stats */}
            <section className={`${styles.card} ${styles.quickStats}`}>
              <div className={styles.cardHeader}>
                <h2 className={styles.cardTitle}>Activity Summary</h2>
                <Link to="/admin/reports" className={styles.cardAction}>Reports</Link>
              </div>
              <div className={styles.quickStatsList}>
                <div className={styles.quickStatItem}>
                  <span className={styles.quickStatLabel}>Avg Score</span>
                  <span className={styles.quickStatValue}>{displayStats.avgScore}%</span>
                </div>
                <div className={styles.quickStatItem}>
                  <span className={styles.quickStatLabel}>This Week</span>
                  <span className={styles.quickStatValue}>--</span>
                </div>
                <div className={styles.quickStatItem}>
                  <span className={styles.quickStatLabel}>Completion Rate</span>
                  <span className={styles.quickStatValue}>--</span>
                </div>
                <div className={styles.quickStatItem}>
                  <span className={styles.quickStatLabel}>Active Classes</span>
                  <span className={styles.quickStatValue}>--</span>
                </div>
              </div>
            </section>

            {/* Quick Actions */}
            <section className={styles.quickActions}>
              {adminActions.map((action) => (
                <Link key={action.id} to={action.to} className={styles.actionCard}>
                  <div className={styles.actionCardHeader}>
                    <div className={styles.actionCardIcon}>{action.icon}</div>
                    <h3 className={styles.actionCardTitle}>{action.title}</h3>
                  </div>
                  <p className={styles.actionCardDescription}>{action.description}</p>
                </Link>
              ))}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
